import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';

import { mapOktaLanguageCode } from '@shure/cloud/shared/services/organizations-store-service';
import { PrismSnackbarService } from '@shure/prism-angular-components';
import { AppLanguageListService } from '@shure/shared/angular/utils/i18n';
import { LanguageDefinition } from '@shure/shared/models';

@Component({
	selector: 'sh-language-selector',
	templateUrl: 'language-selector.component.html',
	styleUrls: ['./language-selector.component.scss'],
	standalone: false
})
export class LanguageSelectorComponent implements OnInit, OnDestroy, OnChanges {
	@Output() public languageChanged: EventEmitter<string> = new EventEmitter();
	@Input() public preferredCultureCode = '';
	@Input() public isDisabled = false;
	@Input() public required = true;
	@Input() public label = '';
	public languages: LanguageDefinition[] = [];
	public languageSelectorForm!: FormGroup;
	private languageCultureCodes: string[] = [];
	protected destroy$: Subject<void> = new Subject<void>();

	constructor(
		private translocoService: TranslocoService,
		private snackBarService: PrismSnackbarService,
		private appLanguageListService: AppLanguageListService
	) {}

	public ngOnInit(): void {
		this.languageSelectorForm = new FormGroup({
			preferredLanguage: new FormControl(
				this.preferredCultureCode ?? '',
				this.required ? Validators.required : null
			)
		});
		if (this.isDisabled) {
			this.languageSelectorForm.get('preferredLanguage')?.disable();
		}
		this.languages = this.appLanguageListService.getLanguageList();
		this.languageCultureCodes = Object.values(mapOktaLanguageCode);
	}

	public ngOnChanges(changes: SimpleChanges): void {
		// eslint-disable-next-line dot-notation
		if (changes['preferredCultureCode']) {
			this.updatePreferredLanguageForm();
		}
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public get preferredLanguage(): FormControl {
		return <FormControl>this.languageSelectorForm?.get('preferredLanguage') ?? new FormControl('');
	}

	public setLocaleAndEmit(cultureCode: string): void {
		if (this.languageCultureCodes.includes(cultureCode)) {
			this.languageChanged.emit(cultureCode);
		} else {
			this.snackBarService.open({
				message: this.translocoService.translate(
					'cloud.shared.notification-preferences.unsupported-language-message'
				),
				actionText: this.translocoService.translate('common.snackbar.ok')
			});

			this.updatePreferredLanguageForm();
		}
	}

	private updatePreferredLanguageForm(): void {
		if (!this.languageSelectorForm) {
			return;
		}
		const cultureCode: string = this.preferredCultureCode;
		this.preferredLanguage.setValue(cultureCode);
		this.languageSelectorForm.markAsPristine();
	}
}
